import Papa from 'papaparse';

import { readDataFile } from 'services/s3Service';

import { normalizeToModelPeriod } from './timePeriodParser';

export type TimeRowOption = {
  index: number;
  label: string;
  mappings: Array<{ key: string; value: string }>;
  matchedCount: number;
};

type LoadTimeRowOptionsParameters = {
  s3FileUrl: string;
  headerNames: string[];
  modelTimeRange: string[];
};

const rowNameFromCells = (row: Array<unknown>): string => {
  const first = row.find((cell) => cell != null && String(cell).trim() !== '');
  if (first == null) return 'Time period row';
  const name = String(first).trim();
  return name.length > 48 ? `${name.slice(0, 45)}…` : name;
};

const parseCsvToRowOptions = (
  csvText: string,
  headerNames: string[],
  modelTimeRange: string[],
): Promise<TimeRowOption[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvText, {
      header: false,
      skipEmptyLines: false,
      complete: (result: Papa.ParseResult<unknown[]>) => {
        const allRows = (result.data as unknown[][]).filter((row) => Array.isArray(row));
        // Always include the first parsed row as a candidate. When the file has headers,
        // users often need to select that header row itself (e.g. Account | Dec 2023 | Jan 2024 ...).
        const startIndex = 0;
        const options = allRows
          .slice(startIndex)
          .map((row, offset) => {
            const actualIndex = startIndex + offset;
            const mappings = headerNames
              .map((header: string, columnIndex: number) => {
                const mappedPeriod = normalizeToModelPeriod(row[columnIndex], modelTimeRange);
                return header && mappedPeriod ? { key: header, value: mappedPeriod } : null;
              })
              .filter(Boolean) as Array<{ key: string; value: string }>;

            return {
              index: actualIndex,
              label: rowNameFromCells(row),
              mappings,
              matchedCount: mappings.length,
            };
          })
          .filter((row) => row.matchedCount > 0);
        resolve(options);
      },
      error: (error: Error) => reject(error),
    });
  });
};

export const loadTimeRowOptions = async ({
  s3FileUrl,
  headerNames,
  modelTimeRange,
}: LoadTimeRowOptionsParameters): Promise<TimeRowOption[]> => {
  const key = s3FileUrl.split('/').pop();
  if (!key) {
    throw new Error('Missing file key');
  }

  const signedUrl = await readDataFile(key, 'blox-data-import');
  const csvText = await fetch(signedUrl).then((response) => response.text());
  return parseCsvToRowOptions(csvText, headerNames, modelTimeRange);
};
